import type { Editor } from '@tiptap/react';
import type { DraftingInsertMode } from './types';
import { createRafContentScheduler } from '../editor/rafContentScheduler';
import { stripPartialHtml } from '../editor/stripPartialHtml';

const BLANK_DOC_RE = /^(<p>\s*<\/p>|<p><br\s*\/?><\/p>|<p><\/p>|\s*)*$/i;

export function stripDraftingHighlights(html: string): string {
  if (!html?.trim()) return html;
  let prev = html;
  let next = html;
  do {
    prev = next;
    next = next
      .replace(/<mark\b[^>]*>([\s\S]*?)<\/mark>/gi, '$1')
      .replace(
        /<span\b[^>]*\bclass=(["'])[^"']*\bhighlight[^"']*\1[^>]*>([\s\S]*?)<\/span>/gi,
        '$2',
      );
  } while (next !== prev);
  return next;
}

export function isDocumentBlank(html: string): boolean {
  const trimmed = (html || '').trim();
  if (!trimmed) return true;
  if (BLANK_DOC_RE.test(trimmed)) return true;
  const text = trimmed.replace(/<[^>]+>/g, '').trim();
  return text.length < 3;
}

export type InsertionStreamHandlers = {
  onToken?: (text: string) => void;
  onFinal?: (html: string) => void;
};

export function applyStreamToRange(
  editor: Editor,
  from: number,
  to: number,
  event: { type: string; data?: { text?: string; html?: string } },
): number {
  let end = to;
  if (event.type === 'token' && event.data?.text?.trim()) {
    editor
      .chain()
      .setTextSelection({ from, to: end })
      .insertContent(event.data.text)
      .run();
    end = editor.state.selection.to;
  }
  if (event.type === 'final' && event.data?.html?.trim()) {
    editor
      .chain()
      .setTextSelection({ from, to: end })
      .insertContent(event.data.html)
      .run();
    end = editor.state.selection.to;
  }
  return end;
}

export function runInsertionForMode(
  editor: Editor,
  insertMode: DraftingInsertMode | string,
  fullDocHtml: string,
  options?: { deleteFrom?: number; deleteTo?: number },
): { from: number; to: number; mode: 'range' | 'setContent' | 'append' } {
  const docBlank = isDocumentBlank(fullDocHtml);
  const sel = editor.state.selection;
  const hasSelection = !sel.empty;

  if (insertMode === 'document_if_blank' && docBlank) {
    return { from: 0, to: 0, mode: 'setContent' };
  }

  if (insertMode === 'selection') {
    if (!hasSelection) {
      throw new Error('Select text in the document before using this command.');
    }
    return { from: sel.from, to: sel.to, mode: 'range' };
  }

  if (options?.deleteFrom != null && options?.deleteTo != null) {
    editor.chain().deleteRange({ from: options.deleteFrom, to: options.deleteTo }).run();
    const pos = options.deleteFrom;
    return { from: pos, to: pos, mode: 'range' };
  }

  if (insertMode === 'cursor' || insertMode === 'smart') {
    if (docBlank && insertMode === 'smart') {
      return { from: 0, to: 0, mode: 'setContent' };
    }
    const pos = sel.empty ? sel.from : sel.to;
    if (!sel.empty && insertMode === 'smart') {
      return { from: sel.from, to: sel.to, mode: 'range' };
    }
    return { from: pos, to: pos, mode: 'range' };
  }

  if (docBlank) {
    return { from: 0, to: 0, mode: 'setContent' };
  }

  const endPos = editor.state.doc.content.size;
  return { from: endPos, to: endPos, mode: 'append' };
}

export async function streamIntoEditor(
  editor: Editor,
  insertMode: DraftingInsertMode | string,
  fullDocHtml: string,
  stream: AsyncIterable<{ type: string; data?: { text?: string; html?: string } }>,
  options?: { deleteFrom?: number; deleteTo?: number },
): Promise<void> {
  const plan = runInsertionForMode(editor, insertMode, fullDocHtml, options);

  if (plan.mode === 'setContent') {
    let accumulated = '';
    let firstTokenReceived = false;
    const scheduler = createRafContentScheduler((content) => {
      if (!content.trim()) return;
      editor.commands.setContent(stripPartialHtml(content), { emitUpdate: false });
    });

    for await (const event of stream) {
      if (event.type === 'token' && event.data?.text) {
        if (!firstTokenReceived) {
          editor.commands.setContent('<p></p>', { emitUpdate: false });
          firstTokenReceived = true;
        }
        accumulated += event.data.text;
        scheduler.schedule(accumulated);
      }
      if (event.type === 'final' && event.data?.html) {
        accumulated = event.data.html;
      }
    }

    scheduler.cancel();
    if (accumulated.trim()) {
      editor.commands.setContent(accumulated, { emitUpdate: true });
    }
    return;
  }

  let from = plan.from;
  let to = plan.to;

  if (plan.mode === 'append' && from > 0) {
    editor.chain().focus().setTextSelection(from).run();
  }

  for await (const event of stream) {
    to = applyStreamToRange(editor, from, to, event);
  }
}
